import { Component } from "@theme/component";
import { ThemeEvents, BundleChangeEvent } from "@theme/events";
import { formatMoneyWithoutTrailingZeros } from "@theme/money-formatting";

/**
 * @typedef {Object} InstallmentsBlockRefs
 * @property {HTMLElement} installmentPrice - The element that displays the per-payment amount
 */

/**
 * `<installments-block>` — provider-agnostic "or 4 interest-free payments of $X" widget.
 *
 * Subscribes to:
 *   - `bundle:change` (scoped to the closest `.shopify-section`) — repaints from
 *     `event.detail.bundlePrice` so the per-payment amount tracks bundle picks.
 *   - `variant:update` — re-reads `event.detail.resource.price` so the widget
 *     keeps up with combined-listing variant changes. Tries to fold in the
 *     active bundle option's discount by querying the section's `<bundle-selector>`
 *     after the variant has settled, mirroring how `assets/sticky-add-to-cart.js`
 *     consumes `getCurrentDetail()`.
 *
 * On mount, calls `bundleSelector.getCurrentDetail()` (if present in the same
 * section) so the very first paint reflects whichever bundle option is currently
 * SSR-default-selected — avoids a flash where the SSR variant price differs
 * from the active bundle's total.
 *
 * @extends {Component<InstallmentsBlockRefs>}
 */
class InstallmentsBlock extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const section = this.closest(".shopify-section");
    if (!section) return;

    section.addEventListener(BundleChangeEvent.eventName, this.#handleBundleChange, { signal });
    section.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // Pull the active bundle's total on mount so the SSR price gets corrected
    // when the SSR-default option is anything other than "single, no extra
    // discount on top of compare-at" (the only case where variant.price ÷ N
    // happens to equal the bundle total).
    this.#applyInitialBundleState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Repaint the per-payment amount from a dispatched `bundle:change` detail.
   * @param {Event} event
   */
  #handleBundleChange = (event) => {
    const detail = /** @type {{ detail: { sectionId: string, bundlePrice: number } }} */ (/** @type {any} */ (event))
      .detail;
    if (!detail) return;
    if (detail.sectionId !== this.dataset.sectionId) return;
    this.#updatePrice(detail.bundlePrice);
  };

  /**
   * Update the unit-price dataset on variant changes, then defer to the
   * post-mount bundle reconciliation so the displayed price reflects the
   * variant's new price *with* the active bundle option's discount applied.
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    const detail =
      /** @type {{ detail: { resource: { id: number, price: number } | null, data: { productId?: string, newProduct?: { id: string | number } } } }} */ (
        /** @type {any} */ (event)
      ).detail;
    if (!detail?.resource) return;

    if (detail.data?.newProduct?.id) {
      this.dataset.productId = String(detail.data.newProduct.id);
    } else if (this.dataset.productId && detail.data?.productId && detail.data.productId !== this.dataset.productId) {
      return;
    }

    const newUnit = Number(detail.resource.price);
    if (Number.isFinite(newUnit)) {
      this.dataset.unitPrice = String(newUnit);
    }

    // The bundle-selector listens for the same `variant:update` event and
    // refreshes its own dataset; queueMicrotask ensures we read AFTER its
    // listener has settled. Same trick `sticky-add-to-cart.js` uses.
    queueMicrotask(() => this.#applyInitialBundleState());
  };

  /**
   * Look up the section's `<bundle-selector>` (if any) and ask it for the
   * currently-checked option's bundle price. Bails silently when no bundle
   * is configured — the SSR variant.price ÷ N value stays as-is.
   */
  #applyInitialBundleState() {
    const section = this.closest(".shopify-section");
    if (!section) return;

    const selector = section.querySelector("bundle-selector");
    if (!selector || typeof (/** @type {any} */ (selector).getCurrentDetail) !== "function") {
      // No bundle in this section — SSR fallback (variant.price ÷ N) is correct.
      const fallback = parseInt(this.dataset.unitPrice || "0", 10);
      if (Number.isFinite(fallback) && fallback > 0) this.#updatePrice(fallback);
      return;
    }

    const detail = /** @type {any} */ (selector).getCurrentDetail();
    if (detail && Number.isFinite(detail.bundlePrice)) {
      this.#updatePrice(detail.bundlePrice);
    }
  }

  /**
   * Repaint the per-payment amount from a total bundle price (in cents).
   *
   * Floors rather than rounds, for two reasons that point the same way. It
   * matches Liquid's `divided_by` on integers, so the client repaint and the
   * SSR value agree instead of disagreeing by a cent on the first paint. And
   * it keeps N payments from summing to more than the price, which rounding up
   * would do — an overstated instalment is a claim we cannot honour.
   *
   * Every caller (the `bundle:change` listener, the no-bundle fallback and the
   * initial bundle state) repaints through here, so the two agree everywhere.
   *
   * @param {number} bundlePriceCents
   */
  #updatePrice(bundlePriceCents) {
    const count = parseInt(this.dataset.installmentCount || "4", 10) || 4;
    const installment = Math.floor(bundlePriceCents / count);

    const priceEl = this.refs.installmentPrice;
    if (!priceEl) return;

    const useCurrency = this.dataset.currencyCodeEnabled === "true";
    const moneyFormat = this.#parseFormat(this.dataset.moneyFormat);
    const moneyWithCurrencyFormat = this.#parseFormat(this.dataset.moneyWithCurrencyFormat);
    const format = useCurrency ? moneyWithCurrencyFormat : moneyFormat;
    const currency = this.dataset.currencyCode || "";

    priceEl.textContent = formatMoneyWithoutTrailingZeros(installment, format, currency);
  }

  /**
   * Mirrors the helper in `bundle-selector.js` — the browser already
   * decodes HTML entities when reading dataset attributes, so we only
   * need to fall back to a default template when the attr is empty.
   *
   * @param {string | undefined} format
   */
  #parseFormat(format) {
    return format && format.length > 0 ? format : "${{amount}}";
  }
}

if (!customElements.get("installments-block")) {
  customElements.define("installments-block", InstallmentsBlock);
}
